// The calendar (scenario D12, screens/CALENDAR.md, PLAN-2026-10-30-the-calendar,
// DIRECTION U30): everything in the league that happens at a time, laid out
// as a calendar, so a quick look says when to be here.
//
// TABLET AND UP, A CALENDAR that starts on Sunday — two weeks by default, one
// week, or the league's own week, kept on the device (B-569) — the width of
// the screen, every entry a card in its own day that says what it is (B-570).
// A PHONE, A DAY AT A TIME under a strip of the week's days, Coming up under
// today.
//
// THE DAYS ARE THE READER'S: the server serves instants and the screen lays
// them on the days of the reader's zone (`days.ts`), asking for exactly the
// days it draws.

import { useEffect, useMemo, useState } from 'react';
import { valueOf, type CalendarView, type Snapshot } from '@fsp/domain';
import { zoneAbbreviation } from '@fsp/domain';
import { useCalendarLayout, type CalendarLayout } from '../data/calendarLayout';
import { useAtLeast } from '../data/detail';
import { deviceZone, useCalendar, useLeagueBase, useReaderZone } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { isDemoLeague, useLeagueNow } from '../data/demo';
import { leagueIdFromPath } from '../data/route';
import { useLocation } from 'react-router-dom';
import { useSim } from '../data/SimProvider';
import { Panel } from '../kit/Panel';
import { Button, IconButton, Segments, Text } from '../kit/ui';
import { useTier } from '../shell/useTier';
import {
  addDays,
  dayKeyOf,
  byDayOf,
  dayStart,
  gridOf,
  leagueWeekday,
  rangeOf,
  stripOf,
  weekOfDay,
} from './calendar/days';
import { CalendarGrid } from './calendar/Grid';
import { CalendarPhone } from './calendar/Phone';
import { entryOf, lineupEntry, whenIn, type Entry } from './calendar/words';
import { FromLeague } from './shared/FromLeague';
import '../styles/screens/calendar.css';

const LAYOUTS: readonly { id: CalendarLayout; label: string }[] = [
  { id: 'two-weeks', label: 'Two weeks' },
  { id: 'one-week', label: 'One week' },
  { id: 'fantasy-week', label: 'Fantasy week' },
];
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOUR = 3600 * 1000;
/** How many things Coming up lists. */
const COMING_UP = 5;

/** The time, a minute at a time: the calendar's today and its Coming up move with it. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function useRouteLeagueIdOf(): string | null {
  return leagueIdFromPath(useLocation().pathname);
}

export function CalendarScreen() {
  const tier = useTier();
  const phone = tier === 'phone';
  const zone = useReaderZone() ?? deviceZone();
  const source = useLeagueSource();
  const sim = useSim();
  const clientNow = useMinute();
  const base = useLeagueBase();
  const numbers = useAtLeast('standard');
  const [layout, setLayout] = useCalendarLayout();
  // The league's week day, learned from its weeks once they are read.
  const [weekday, setWeekday] = useState<number | null>(null);
  // A LEAGUE HELD STILL (the demo league, O6) has its own now: the calendar's
  // today is its day, not the wall's. Every other league's moves with the
  // minute, whatever age the answer on screen is.
  const leagueNow = useLeagueNow();
  const held = isDemoLeague(useRouteLeagueIdOf());
  const now = source === 'sim' ? sim.now : held ? leagueNow : clientNow;
  const today = dayKeyOf(now, zone);
  const [picked, setPicked] = useState<string | null>(null);
  const day = picked ?? today;
  const startDay = layout === 'fantasy-week' ? (weekday ?? 0) : 0;

  const rows = phone ? [stripOf(day, startDay)] : gridOf(day, layout, weekday ?? 0);
  const shown = rows.flat();
  // A PHONE READS A WEEK PAST ITS STRIP, so an empty Saturday can still say
  // what is next.
  const asked = phone ? [...shown, ...stripOf(addDays(shown[6]!, 1), startDay)] : shown;
  const range = rangeOf(asked, zone);
  const snapshot = useCalendar(range.from, range.to);
  const view = valueOf(snapshot);

  useEffect(() => {
    if (view !== null && view.weeks.length > 0) setWeekday(leagueWeekday(view.weeks, zone, now));
  }, [view, zone, now]);

  const body = (v: CalendarView | null) => (
    <CalendarBody
      view={v}
      loading={v === null}
      phone={phone}
      tier={tier}
      rows={rows}
      day={day}
      today={today}
      now={now}
      zone={zone}
      base={base}
      numbers={numbers}
      layout={layout}
      weekday={weekday}
      practice={source === 'sim'}
      onLayout={setLayout}
      onPick={setPicked}
    />
  );
  return (
    <div className="fsp-screen fsp-cal-screen" data-tier={tier}>
      {snapshot.state === 'failed' ? (
        <FromLeague snapshot={snapshot as Snapshot<CalendarView>}>{(v) => body(v)}</FromLeague>
      ) : (
        body(view)
      )}
    </div>
  );
}

function CalendarBody({
  view,
  loading,
  phone,
  tier,
  rows,
  day,
  today,
  now,
  zone,
  base,
  numbers,
  layout,
  weekday,
  practice,
  onLayout,
  onPick,
}: {
  view: CalendarView | null;
  loading: boolean;
  phone: boolean;
  tier: string;
  rows: string[][];
  day: string;
  today: string;
  now: number;
  zone: string;
  base: string;
  numbers: boolean;
  layout: CalendarLayout;
  weekday: number | null;
  practice: boolean;
  onLayout: (layout: CalendarLayout) => void;
  onPick: (day: string) => void;
}) {
  // THE LEAGUE'S OWN WEEKS: a week of the sport's it does not play (before a
  // league begun mid-season, after its last) is no week of the league's.
  const weeks = useMemo(() => (view?.weeks ?? []).filter((w) => w.kind !== 'off'), [view]);
  // A WEEK'S LINEUP THAT CANNOT SCORE is a card of its own, on the day to fix
  // it (`lineupEntry`), before anything else at its instant.
  const entries = useMemo(
    () =>
      [
        ...weeks.flatMap((w) => lineupEntry(w, now) ?? []),
        ...(view?.events ?? []).map((e) => entryOf(e, weeks, numbers)),
      ].sort((a, b) => a.at - b.at),
    [view, weeks, numbers, now],
  );
  const byDay = useMemo(() => byDayOf(entries, zone), [entries, zone]);
  // The sport's games on each day, from the weeks' slates.
  const slate = useMemo(() => {
    const out = new Map<string, number>();
    for (const w of weeks)
      for (const k of w.slate) {
        const key = dayKeyOf(Date.parse(k.at), zone);
        out.set(key, (out.get(key) ?? 0) + k.games);
      }
    return out;
  }, [weeks, zone]);
  const entriesOf = (d: string): readonly Entry[] => byDay.get(d) ?? [];
  const nextAfter = (d: string): Entry | null => {
    const end = dayStart(addDays(d, 1), zone);
    return entries.find((e) => e.at >= end && !e.timeless) ?? null;
  };
  const week = weekOfDay(day, zone, weeks);
  const preDraft = view !== null && view.weeks.length === 0 && view.league.status === 'setup';

  const notes = (
    <>
      {practice ? (
        <Text size="sm" tone="secondary" block>
          The practice league plays one week.
        </Text>
      ) : null}
      {preDraft ? (
        <Text size="sm" tone="secondary" block>
          Your players’ games come onto the calendar after the draft.
        </Text>
      ) : null}
    </>
  );

  const comingUp = upcoming(entries, now);
  const soon = <ComingUp items={comingUp} zone={zone} onPick={onPick} />;

  if (phone)
    return (
      <div className="fsp-cal">
        {notes}
        <CalendarPhone
          strip={rows[0]!}
          day={day}
          today={today}
          zone={zone}
          week={week}
          entriesOf={entriesOf}
          next={nextAfter(day)}
          base={base}
          numbers={numbers}
          loading={loading}
          leagueWeek={layout === 'fantasy-week'}
          leagueWeekday={weekday === null || weekday === 0 ? null : weekday}
          onPick={onPick}
          onLeagueWeek={(on) => onLayout(on ? 'fantasy-week' : 'two-weeks')}
          zoneName={zoneAbbreviation(zone, now)}
          now={now}
          comingUp={day === today && comingUp.length > 0 ? soon : null}
        />
      </div>
    );

  const days = rows.flat();
  const step = days.length;
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const fmt = (d: string) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: zone }).format(
      new Date(dayStart(d, zone) + 12 * HOUR),
    );
  const away = !days.includes(today);
  return (
    <div className="fsp-cal" data-tier={tier}>
      <div className="fsp-cal-head">
        <div className="fsp-cal-nav">
          <IconButton
            icon="previous"
            label={step === 7 ? 'The week before' : 'The two weeks before'}
            variant="quiet"
            onClick={() => onPick(addDays(day, -step))}
          />
          <h1 className="fsp-cal-range">
            {fmt(first)} – {fmt(last)}
          </h1>
          <IconButton
            icon="next"
            label={step === 7 ? 'The week after' : 'The two weeks after'}
            variant="quiet"
            onClick={() => onPick(addDays(day, step))}
          />
          {away ? (
            <Button size="xs" variant="soft" onClick={() => onPick(today)}>
              Today
            </Button>
          ) : null}
          <span className="fsp-cal-zone fsp-muted">Times in {zoneAbbreviation(zone, now)}</span>
        </div>
        <Segments
          items={LAYOUTS.map((l) =>
            l.id === 'fantasy-week' && weekday !== null && weekday !== 0
              ? { ...l, label: `Fantasy week (${DOW[weekday]!.slice(0, 3)})` }
              : l,
          )}
          value={layout}
          onChange={onLayout}
          label="Layout"
        />
      </div>
      {notes}
      <CalendarGrid
        rows={rows}
        zone={zone}
        today={today}
        now={now}
        weeks={weeks}
        entriesOf={entriesOf}
        slateOf={(d) => slate.get(d) ?? 0}
        numbers={numbers}
        roomy={layout === 'one-week' && tier !== 'tablet'}
        base={base}
      />
    </div>
  );
}

/** `now` for a card placed on today rather than at an instant. */
type Soon = { key: string; at: number; now: boolean; title: string; needsMe: boolean };

/** A phone's quick look under today: the next few things, each opening its day. */
function ComingUp({
  items,
  zone,
  onPick,
}: {
  items: readonly Soon[];
  zone: string;
  onPick: (day: string) => void;
}) {
  return (
    <Panel title="Coming up" bodyClass="fsp-cal-panel">
      {items.length === 0 ? (
        <Text size="sm" tone="secondary" block>
          Nothing else on the calendar for now.
        </Text>
      ) : (
        <ul className="fsp-cal-soon">
          {items.map((c) => (
            <li key={c.key}>
              <button
                type="button"
                className="fsp-cal-soon-row"
                data-needs={c.needsMe || undefined}
                onClick={() => onPick(dayKeyOf(c.at, zone))}
              >
                <span className="fsp-cal-soon-when">{c.now ? 'Now' : whenIn(c.at, zone)}</span>
                <span className="fsp-cal-soon-what">{c.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Coming up: the next few things from now, a lineup that cannot score among them. */
function upcoming(entries: readonly Entry[], now: number): Soon[] {
  return entries
    .filter((e) => e.at >= now)
    .slice(0, COMING_UP)
    .map((e) => ({
      key: e.key,
      at: e.at,
      now: e.timeless && e.at <= now,
      title: e.title,
      needsMe: e.needsMe,
    }));
}
